/**
 * Custom hook for managing PDFViewerPanel state
 *
 * Extracted from PDFViewerPanel.tsx
 */

import { useState, useEffect, useRef } from 'react';
import logger from '../../../utils/simpleLogger';
import apiService from '../../../services/api';
import type {
  UsePDFViewerPanelStateProps,
  UsePDFViewerPanelStateReturn,
} from '../types/pdf-viewer-panel.types';

/**
 * Manages state for PDFViewerPanel (diagnosis, errors, page, references)
 */
export function usePDFViewerPanelState({
  citationToView,
  onPageChange,
}: UsePDFViewerPanelStateProps): UsePDFViewerPanelStateReturn {
  const [diagnosis, setDiagnosis] = useState<any>(null);
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [referencesExpanded, setReferencesExpanded] = useState(false);
  const isUpdatingFromContent = useRef(false);

  // Sincronizar currentPage cuando cambia citationToView.page
  useEffect(() => {
    if (citationToView?.page) {
      logger.info('📄 PDFViewerPanel - Sincronizando página desde cita:', citationToView.page);
      setCurrentPage(citationToView.page);
    }
  }, [citationToView?.page, citationToView?.documentPath]);

  // Diagnosis disabled: evitaba ruido en consola (Failed to fetch) y no es necesario en runtime.
  // Handler para cuando el usuario cambia de página en el PDF
  const handlePageChange = (newPage: number) => {
    if (isUpdatingFromContent.current) {
      // Evitar loop infinito
      isUpdatingFromContent.current = false;
      return;
    }

    logger.info(`📄→📝 PDFViewer: Usuario cambió a página ${newPage}`);
    setCurrentPage(newPage);
    onPageChange?.(newPage);
  };

  return {
    diagnosis,
    showDiagnosis,
    setShowDiagnosis,
    pdfError,
    setPdfError,
    currentPage,
    setCurrentPage,
    referencesExpanded,
    setReferencesExpanded,
    handlePageChange,
  };
}
